import React, { useEffect, useState, FocusEvent, useRef } from "react";
import { IActionHandler, OrgInfo, AdminUserBase, UserProfile, AdminApprovalGroup, DataExportRequest, UserShard } from "@preveil-api";
import {
  AdminErrorMessages, AdminSuccessMessages, useGetOrgInfo, useRequestExportMutation, Account, GlobalErrorMessages, MessageAnchors, MessageHandlerDisplayType, Message, useAppDispatch,
  dayjs, useGetExportRequestsMutation, useAppSelector, useDeleteAdminRequestMutation, useGetOrgRequestsMutation, useStartExportMutation, useGetExportProgressMutation, useCancelExportMutation, useDeleteExportRequestMutation, isSameUser,
  DataExportRequestStatus, DataExportAllContent, dropdown_labels, GetOrgRequestsApiResponse, RequestExportApiResponse, StartExportApiResponse, GetExportRequestsApiResponse, ExportRequest, OrganizationInformation, useGetOrgUsersShardStatusesQuery,
  UserResultsMode
} from "src/common";
import { Loading } from "src/components";
import { GetLocationModal, Header, RequestCompleted, RequestProgress, SetApprovalGroup, SidePanel } from ".";
import { ApproveStep } from "./approval-step";
import { GetStarted } from "./get-started-step";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
}

// Description: Data Export Component - allows admins to download a decrypted copy of your organization's data. 
function DataExportComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const users_with_export_shards = useAppSelector((state: RootState) => state.admin.users_with_export_shards);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [show_location_modal, setShowLocationModal] = useState<boolean>(false);
  const [show_side_panel, setShowSidePanel] = useState<boolean>(false);
  const [data_export_approval_group, setDataExportApprovalGroup] = useState<AdminApprovalGroup>(); // Assigned Approval Group for Data Export.
  const [org_users, setOrgUsers] = useState<AdminUserBase[]>([]); // Users in the Organization.
  const [approvers, setApprovers] = useState<UserProfile[]>([]); // Approvers in the Data Export Approval Group.
  const [already_approved, setAlreadyApproved] = useState<UserProfile[]>([]); // Users who have approved the Data Export Request.
  const [export_request, setExportRequest] = useState<ExportRequest>(); // Data Export Request.
  const [step, setStep] = useState<keyof typeof DataExportRequestStatus>(DataExportRequestStatus.not_started); // Data Export Request Status Step.
  const [drop_dir, setDropDir] = useState<string>(""); // Directory Path for the Data Export Request.
  const [data_export_content, setDataExportContent] = useState<DataExportAllContent>({ mail: true, drive: true, include_all_versions: false, logs: false, acl_report: false });
  const [start_date, setStartDate] = useState<Date | string>(dayjs().subtract(2, "week").toDate().toISOString());
  const [end_date, setEndDate] = useState<Date | string>(dayjs().toDate().toISOString());
  const [selected_range, setSelectedRange] = useState<string>("default");
  const polling_interval_id = useRef<any | null>(null);

  const [requestExport] = useRequestExportMutation();
  const [getExportRequests] = useGetExportRequestsMutation();
  const [deleteExportRequest] = useDeleteExportRequestMutation();
  const [deleteAdminRequest] = useDeleteAdminRequestMutation();
  const [getOrgRequests] = useGetOrgRequestsMutation();
  const [startExport] = useStartExportMutation();
  const [getExportProgress] = useGetExportProgressMutation();
  const [cancelExport] = useCancelExportMutation();
  const { data: shard_statuses, error: shard_statuses_error, isFetching, refetch } = useGetOrgUsersShardStatusesQuery({
    account_ids: Account.getAccountIdentifiers(current_account),
    body: {
      entity_id: org_info.org_id
    }
  }, { refetchOnMountOrArgChange: true, skip: !data_export_approval_group }); // Skip this call if there is not export_approval_group (it will fail).
  const { data, error, refresh } = useGetOrgInfo(current_account, org_info, UserResultsMode.lightweight);

  useEffect(() => {
    fetchOrgData();
    return () => {
      dispatch(adminActions.setSelected([]));
      handleSetInterval();
    };
  }, []);

  useEffect(() => {
    // Description: Resume the polling if the export request is running or started, and the interval is not already set. (when refreshing the page or the component is mounted)
    const should_resume_polling = (step === DataExportRequestStatus.running || step === DataExportRequestStatus.started) && !!export_request?.request_id && !polling_interval_id.current;
    should_resume_polling && handleSetInterval(export_request.request_id);

  }, [step, export_request]);

  useEffect(() => {
    if (error) {
      DataExportRequests.handlePageError({ message: AdminErrorMessages.default, stack: error });
    }

    // Find the export approval group
    // if there are not any, set the step to not initialized
    if (data) {
      const export_approval_group = data.approval_groups?.find((group) => group.roles?.includes("export_approval_group"));
      if (!export_approval_group || _.isEmpty(export_approval_group)) {
        setStep(DataExportRequestStatus.not_init);
        setIsLoading(false);
      } else {
        setDataExportApprovalGroup(export_approval_group);
        setApprovers(export_approval_group.approvers);
      }
    }
  }, [data, error]);

  useEffect(() => {
    if (shard_statuses_error) {
      DataExportRequests.handlePageError({ message: AdminErrorMessages.default, stack: shard_statuses_error });
    }

    // After the data_export_approval_group is set in state
    // We'll filter out the users that have not submitted their shards.
    if (data_export_approval_group && data?.lightweight_org_users && shard_statuses) {
      let users_with_export_shards: string[] = [];

      if (shard_statuses?.user_statuses) {
        const { total_approvers, user_statuses } = shard_statuses;
        // Note: /export/shard_statuses returns deleted accounts so we want to filter those out first.
        // By using the org_users data we can make sure to only include the active users in the organization before we check the shard submission status.
        const active_org_users = data.lightweight_org_users.filter((u: AdminUserBase) => !!u.claimed_at && !u.deleted);
        users_with_export_shards = user_statuses
          .filter((user: UserShard) =>
            user.shards_submitted === total_approvers &&
            active_org_users.some((org_user: AdminUserBase) => isSameUser(org_user.user_id, user.user_id))
          )
          .map((user: UserShard) => user.user_id);
        dispatch(adminActions.setUsersWithExportShards(users_with_export_shards));
      }
      handleOrgInfoData(data, users_with_export_shards);
    }
  }, [data_export_approval_group, shard_statuses, shard_statuses_error, data]);

  function fetchOrgData() {
    return refresh({
      history: false,
      status: "pending",
      hideExpired: true,
      limit: 200
    });
  }

  async function handleOrgInfoData(data: OrganizationInformation, users_with_export_shards: string[]) {
    if (!!data_export_approval_group) {
      const { lightweight_org_users } = data;
      // TODO: If we get support/fix for the deleted users : Remove the !user.deleted and make sure to include the deleted users in useGetOrgInfo hook.
      const _org_users = lightweight_org_users.filter((user) => {
        return !!user.claimed_at && !user.deleted;
      });
      setOrgUsers(_org_users);
      // we want to filter the org_users to only include the users that have submitted their shards.
      const valid_selected_users = _org_users
        .filter((user) => users_with_export_shards.some((id) => isSameUser(id, user.user_id)))
        .map((user) => user.user_id);
      dispatch(adminActions.setSelected(valid_selected_users));
      await fetchOrgRequests(data_export_approval_group);
    } else {
      setStep(DataExportRequestStatus.not_init);
      setIsLoading(false);
    }
  }

  // Description: Get the export request for the current user (Init state or Cancelled state)
  // We need to get the approvers_info for the current request if there is an actual request under current user.
  // If one of the approvers already approved the request it will be return under approvers_info.
  async function getCurrentUserExportRequests(_approvers: UserProfile[], existing_request?: ExportRequest) {
    if (_approvers) {
      await getExportRequests({
        userId: current_account.user_id
      })
        .unwrap()
        .then((response: GetExportRequestsApiResponse) => {
          if (!_.isEmpty(response)) {
            const keys = Object.keys(response);
            const { approvers_info, status, request, dropdir } = response[keys[0]];
            const request_id = request?.request_id;
            const _request = DataExportRequestStatus.init === status || DataExportRequestStatus.cancelled === status || DataExportRequestStatus.started === status ? (!!request && request.request_payload ? { ...JSON.parse(request.request_payload), request_id } : undefined) : request;
            const approvers_info_ids = !!approvers_info ? Object.keys(approvers_info) : [];
            const is_requester_found = approvers_info_ids.some((approver_id) => isSameUser(approver_id, current_account.user_id));
            const requester_is_approver: boolean = _approvers.filter((approver: UserProfile) => isSameUser(approver.address, current_account.user_id)).length > 0;
            const current_account_found = _approvers
              .filter((approver: UserProfile) => isSameUser(approver.address, current_account.user_id))
              .map((approver: UserProfile) => approver.address);
            const current_approvers_shards = current_account_found.length > 0 && !is_requester_found ? [...current_account_found, ...approvers_info_ids] : approvers_info_ids;
            const current_users_approved = current_approvers_shards.length > 0 ? current_approvers_shards
              .map((_approver: string) =>
                _approvers.find((approver: UserProfile) => isSameUser(approver.address, _approver))
              )
              .filter((user): user is UserProfile => Boolean(user)) : [];
            is_requester_found && !requester_is_approver && current_users_approved.push({ name: current_account.display_name, address: current_account.user_id, role: "admin" });
            const { data, timestamp, expiration, user_id, protocol_version, type } = _request;
            const { from, until, users, include_emails, include_files, include_logs, include_org_acl_report, include_all_versions } = data;
            const new_request = {
              request_id,
              status,
              requester: user_id,
              timestamp,
              type,
              metadata: {
                from,
                include_emails,
                include_files,
                include_all_versions,
                include_logs,
                include_org_acl_report,
                until,
                include_drive: true,
                users
              },
              expiration: dayjs.utc(expiration).utcOffset(new Date().getTimezoneOffset(), true).local().format("lll"),
              protocol_version
            };
            setAlreadyApproved(current_users_approved);
            !!status && setStep(status as keyof typeof DataExportRequestStatus);
            // Description: After the export request has been approved (init status)
            // we need to set the export_request state with the new request_id coming from /exportsRequests
            // We also need to set the export_request state if the request is in cancelled state. If the status is (cancelled)
            // We need to allow the user to start a new export request.
            if (!export_request && (new_request.status === DataExportRequestStatus.init || new_request.status === DataExportRequestStatus.cancelled || new_request.status === DataExportRequestStatus.started)) {
              setExportRequest(new_request);
              dropdir && !drop_dir && setDropDir(dropdir);
            }
            setIsLoading(false);
          } else {
            !existing_request && setStep(DataExportRequestStatus.not_started);
            setIsLoading(false);
          }
        });
    }
  }

  async function fetchOrgRequests(export_approval_group: AdminApprovalGroup) {
    await getOrgRequests({
      userId: current_account.user_id,
      orgId: org_info.org_id,
      hideExpired: true,
      limit: 200,
      offset: null,
      status: "pending"
    })
      .unwrap()
      .then((response: GetOrgRequestsApiResponse) => {
        const { requests } = response;
        const _export_request = requests?.find(request => request.type === "export" && isSameUser(request.requester, current_account.user_id));
        if (!!_export_request) {
          const { status, requester } = _export_request;
          !!status && setStep(status as keyof typeof DataExportRequestStatus);
          const request = { ..._export_request, metadata: _export_request.metadata as DataExportRequest };
          setExportRequest(request);
          if (isSameUser(requester, current_account.user_id)) {
            const get_requester_profile = export_approval_group.approvers.find((approver) => isSameUser(approver.address, requester));
            const is_requester_in_approved_list = already_approved.filter((approver) => isSameUser(approver.address, requester)).length > 0;
            !is_requester_in_approved_list && !!get_requester_profile && setAlreadyApproved([...already_approved, get_requester_profile]);
          }
          !!status && status === DataExportRequestStatus.pending && getCurrentUserExportRequests(export_approval_group.approvers, request);
        } else {
          getCurrentUserExportRequests(export_approval_group.approvers);
        }
      });
  }

  // Description: Delete the export request/cancel export request at any step until the export is process
  // if at least one of the approvers has approved the request (that is not the requester if the requester is in the approvers group)
  // we'll first use this call and then proceed to deleteAdminRequest call if the request is not fully approved.
  async function deleteDataExportRequest(request_id: string) {
    const approvers_shards = already_approved.filter((approver) => !isSameUser(approver.address, current_account.user_id));
    if (approvers_shards.length > 0) {
      try {
        await deleteExportRequest({ exportId: request_id, userId: current_account.user_id, orgId: org_info.org_id })
          .unwrap()
          .then(() => {
            already_approved.length === data_export_approval_group?.required_approvers && clearState();
          });
      } catch (error) {
        const params = { message: AdminErrorMessages.error_delete_existing_data_export, stack: error };
        DataExportRequests.handlePageError(params);
      }
    }
    if (already_approved.length !== data_export_approval_group?.required_approvers) {
      await deleteAdminRequest({ requestId: request_id, orgId: org_info.org_id, userId: current_account.user_id })
        .unwrap()
        .then(() => {
          clearState();
        })
        .catch((error) => {
          const params = { message: AdminErrorMessages.error_cancel_export_request, stack: error };
          DataExportRequests.handlePageError(params);
        });
    }
  }

  // Description: Clear the state after the export request is deleted.
  function clearState(clear_only?: boolean) {
    setStep(DataExportRequestStatus.not_started);
    setAlreadyApproved([]);
    setExportRequest(undefined);
    setDataExportContent({
      mail: true,
      drive: true,
      logs: false,
      acl_report: false,
      include_all_versions: false
    });
    setStartDate(dayjs().subtract(2, "week").toDate().toISOString());
    setEndDate(dayjs().toDate().toISOString());
    !clear_only && dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_cancel_export_request)));
  }

  // Description: Get the data export request started.
  async function getStartedRequestDataExport() {
    setIsLoading(true);
    const selected_users = org_users.filter((user: AdminUserBase) => {
      const is_selected = selected.some((selected_id: string) => isSameUser(selected_id, user.user_id));
      const has_shards = users_with_export_shards.some((user_with_shard: string) => isSameUser(user_with_shard, user.user_id));
      return is_selected && has_shards;
    });
    const users = selected_users.map((user: AdminUserBase) => {
      const { key_range } = user;
      return { user_id: user.user_id, key_version: key_range[key_range.length - 1] };
    });
    const { mail, drive, logs, acl_report, include_all_versions } = data_export_content;
    await requestExport({
      userId: current_account.user_id,
      orgId: org_info.org_id,
      payload: {
        from: start_date as string,
        include_emails: mail,
        include_files: drive,
        include_logs: logs,
        include_org_acl_report: acl_report,
        include_all_versions,
        until: end_date as string,
        users
      }
    }).unwrap()
      .then((response: RequestExportApiResponse) => {
        const { request } = response;
        if (request && !_.isEmpty(request) && request.status) {
          const { status, requester, metadata } = request;
          setExportRequest(request);
          if (status === DataExportRequestStatus.pending) {
            if (!!metadata) {
              const { include_emails, include_files, include_logs, include_org_acl_report, include_all_versions } = metadata;
              const requester_approver = approvers.filter((approver) => isSameUser(approver.address, requester));
              setStep(status);
              requester_approver.length === 1 && setAlreadyApproved(requester_approver);
              setDataExportContent(prev_state => ({
                ...prev_state,
                mail: include_emails,
                drive: include_files,
                include_all_versions,
                logs: include_logs,
                acl_report: include_org_acl_report
              }));
              setIsLoading(false);
            }
          }
        }
      })
      .catch((error) => {
        const params = { message: AdminErrorMessages.error_start_data_export, stack: error };
        setIsLoading(false);
        DataExportRequests.handlePageError(params);
      });
  }

  function handleSetInterval(export_id?: string) {
    if (!!export_id && !polling_interval_id.current) {
      polling_interval_id.current = setInterval(() => {
        getDataExportProgress(export_id);
      }, 3000);
    } else if (!export_id && polling_interval_id.current) {
      clearInterval(polling_interval_id.current);
      polling_interval_id.current = null;
    }
  }

  // Description: Start the export request: This is when the user has selected the drop directory and the request is ready to be started.
  async function startExportRequest(export_id: string, drop_dir: string) {
    await startExport({
      userId: current_account.user_id,
      orgId: org_info.org_id,
      exportId: export_id,
      dropDir: drop_dir
    })
      .unwrap()
      .then((response: StartExportApiResponse) => {
        const { dropdir } = response;
        setStep(DataExportRequestStatus.running);
        setShowLocationModal(false);
        dropdir && setDropDir(dropdir);
        handleSetInterval(export_id);
      })
      .catch((error) => {
        const message = error?.data ? parseDataExportErrorMessage(error.data) : "";
        const params = { message, stack: error };
        DataExportRequests.handlePageError(params);
      });
  }

  // Description: Parse the error response from start data export
  // and return the appropriate error message.
  function parseDataExportErrorMessage(error: any) {
    let message = AdminErrorMessages.error_start_data_export;
    try {
      const { errors } = error;
      const error_cause = errors[0].cause;
      if (error_cause.includes("file exists")) {
        message = AdminErrorMessages.error_export_file_exists;
      } else if (error_cause.includes("permission denied")) {
        message = AdminErrorMessages.error_export_permission_denied;
      } else if (error_cause.includes("no such file or directory")) {
        try {
          const entered_path = /\/.*\//.exec(error_cause)?.[0];
          if (entered_path) {
            message = AdminErrorMessages.error_export_entered_path_no_found.replace(MessageAnchors.message_content, entered_path);
          } else {
            message = AdminErrorMessages.error_export_path_not_found;
          }
        } catch (error) {
          message = AdminErrorMessages.error_export_path_not_found;
        }
      }
    } catch (error) {
      return message;
    }
    return message;
  }

  // Description: Get the data export progress.
  // After the export process has started we need to keep polling the server to get the progress of the export.
  // it will stop when this call returns an error (job no longer running).
  // Note: we need to verify what other errors we can catch from Filesync and handle them accordingly.
  // Right now it's returning 500 error when the export is complete.
  async function getDataExportProgress(export_id: string) {
    await getExportProgress({
      export_id,
      user_id: current_account.user_id
    })
      .unwrap()
      .then((_) => {
        if (step !== DataExportRequestStatus.running) {
          setStep(DataExportRequestStatus.running);
        }
      })
      .catch(() => {
        is_loading && setIsLoading(false);
        setStep(DataExportRequestStatus.complete);
        dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_completing_export)));
        handleSetInterval();
      });
  }

  // Description: Cancel the data export while it's running.
  async function cancelExportRequest(export_id: string) {
    await cancelExport({
      userId: current_account.user_id,
      orgId: org_info.org_id,
      exportId: export_id
    })
      .unwrap()
      .then(() => {
        handleSetInterval();
        setStep(DataExportRequestStatus.cancelled);
        const success_message = AdminSuccessMessages.success_stop_data_export_process;
        dispatch(uiActions.handleSetMessage(new Message(success_message)));
      })
      .catch((error) => {
        const params = { message: AdminErrorMessages.error_cancel_export_request, stack: error };
        DataExportRequests.handlePageError(params);
      });
  }

  const DataExportRequests = {
    handleGetCurrentUserExportRequest: () => {
      getCurrentUserExportRequests(approvers, export_request);
    },
    handleRequestDataExport: () => {
      getStartedRequestDataExport();
    },
    handleSelectDataExportContent: (params: { e: FocusEvent<HTMLInputElement>, id: string }) => {
      const { e, id } = params;
      setDataExportContent((prev_state) => {
        return {
          ...prev_state,
          [id]: e.target.checked
        };
      });
    },
    handleShowModal: async () => {
      setShowLocationModal(!show_location_modal);
    },
    handleShowSideNavMenu: async () => {
      setShowSidePanel(!show_side_panel);
    },
    handleStartDataExport: (drop_dir: string) => {
      const request_id = export_request?.request_id;
      !!request_id && startExportRequest(request_id, drop_dir);
    },
    handleDateSelector: ({ start = new Date(), end = new Date(), selected_range = "default" }: { start?: Date; end?: Date; selected_range?: string }) => {
      setStartDate(start.toISOString());
      setEndDate(end.toISOString());
      setSelectedRange(selected_range);
    },
    handleCancel: () => {
      const request_id = export_request?.request_id;
      !!request_id ? deleteDataExportRequest(request_id) : DataExportRequests.handlePageError({ message: AdminErrorMessages.error_delete_data_export_request_no_id, stack: request_id });
      setSelectedRange("default");
    },
    handleFinish: () => {
      clearState(true);
      setSelectedRange("default");
    },
    handleStopDataExportRunning: () => {
      const export_id = export_request?.request_id;
      if (!!export_id) {
        cancelExportRequest(export_id);
      } else {
        const params = { message: AdminErrorMessages.error_delete_existing_data_export, stack: { export_id, user_id: current_account.user_id } };
        handleSetInterval();
        DataExportRequests.handlePageError(params);
      }
    },
    handlePageError: (params: { message: string, stack?: any, log?: boolean }) => {
      const type = params.log ? MessageHandlerDisplayType.logger : MessageHandlerDisplayType.toastr;
      dispatch(uiActions.handleRequestErrors(new Message(params.message, type), params.stack));
    },
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((DataExportRequests as any)[callback] instanceof Function) {
      (DataExportRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      DataExportRequests.handlePageError({ message, stack: action_obj });
    }
  }

  function ViewUi(step: string) {
    switch (step) {
      case DataExportRequestStatus.not_init:
        return <SetApprovalGroup />;
      case DataExportRequestStatus.not_started:
        const users_label = selected.length === org_users.length && org_users.length > 0 ? dropdown_labels.users : `${selected.length} of ${org_users.length} Users`;
        const is_date_range_selected = !!start_date && !!end_date;
        const date_range_label = selected_range === "forever" ? "Forever" : (is_date_range_selected ? `${dayjs(start_date).format("MM/DD/YYYY")} - ${dayjs(end_date).format("MM/DD/YYYY")}` : "");
        const is_get_started_disabled = selected.length === 0 || !is_date_range_selected || users_with_export_shards.length === 0;
        return !!data_export_approval_group &&
          <GetStarted
            is_loading={isFetching}
            refetchUsersShardStatuses={refetch}
            users_label={users_label}
            date_range_label={date_range_label}
            is_get_started_disabled={is_get_started_disabled}
            org_users={org_users}
            approval_group={data_export_approval_group}
            handleAction={handlePageActions}
            content={data_export_content}
          />;
      case DataExportRequestStatus.init:
      case DataExportRequestStatus.pending:
      case DataExportRequestStatus.cancelled:
        const required_approvers = data_export_approval_group?.required_approvers;
        return !!export_request ? <ApproveStep
          export_request={export_request}
          step={step}
          current_account={current_account}
          approvers={approvers}
          approved_users={already_approved}
          required_approvers={required_approvers}
          handleAction={handlePageActions}
        /> : null;
      case DataExportRequestStatus.started:
      case DataExportRequestStatus.running:
      case DataExportRequestStatus.complete:
        const timeframe = `${dayjs(export_request?.metadata?.from).format("MM/DD/YYYY")} - ${dayjs(export_request?.metadata?.until).format("MM/DD/YYYY")}`;
        return !!export_request && !!timeframe && <RequestCompleted drop_dir={drop_dir} export_status={step} export_request={export_request} timeframe={timeframe} org_users={org_users} />;
      default:
        return null;
    }
  }

  return (
    <div className="content-wrapper">
      <Header
        handleAction={handlePageActions}
        step={step}
        approvals_complete={already_approved.length === data_export_approval_group?.required_approvers}
      />
      <GetLocationModal
        show={show_location_modal}
        handleAction={handlePageActions}
      />
      {!is_loading && step !== DataExportRequestStatus.not_init && step !== DataExportRequestStatus.not_started &&
        <RequestProgress status={DataExportRequestStatus[step as unknown as keyof typeof DataExportRequestStatus]} />
      }
      {is_loading ? <Loading /> :
        <>{ViewUi(step)}</>
      }
      {show_side_panel && !!export_request && <SidePanel export_request={export_request} handleAction={handlePageActions} org_users={org_users} />}
    </div>
  );
}

export default React.memo(DataExportComponent);
